import Environment from "nexia_mobile/environment";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Store, { setValueAtPath } from "nexia_mobile/flux_like/store";
import Platform from "nexia_mobile/platform";
import LocalSettings from "nexia_mobile/models/local_settings";
import { showConfirmationDialog } from "nexia_mobile/components/Dialogs/ConfirmationDialog";
import VognitionHomeProfile from "nexia_mobile/vognition_home_profile";

const isVognitionSupported =
  Platform.isVognitionSupported() && !Environment.isCommercial();

let profileUpdated = false;
let shouldStartOnProfileUpdated = false;
let previousProfile;
Dispatch.on("UPDATE_VOGNITION_HOME_PROFILE", (location) => {
  if (!isVognitionSupported) {
    return;
  }
  if (!Store.appState) {
    return;
  }
  if (Store.appState.inDemoMode || !LocalSettings.getShowVognition()) {
    return;
  }
  let profile = new VognitionHomeProfile(location);
  if (profile.noDevices() || profile.isEqual(previousProfile)) {
    previousProfile = profile;
    return;
  }
  previousProfile = profile;
  Platform.setVognitionHomeProfile(profile)
    .then(() => {
      profileUpdated = true;
      if (shouldStartOnProfileUpdated) {
        Dispatch("START_VOGNITION_LISTENER");
        shouldStartOnProfileUpdated = false;
      }
    })
    .catch(() => {
      /* ??? */
    });
});

let vognitionListenerTimeout;
function startListenerWithTimeout() {
  clearTimeout(vognitionListenerTimeout);
  Platform.runVognitionListener();
  vognitionListenerTimeout = setTimeout(
    () => Platform.stopVognitionListener(),
    10000
  );
}

function showVoicePermissionInstructionDialog() {
  showConfirmationDialog({
    message:
      "You must grant Nexia permission to use your microphone in order to use voice control.",
    confirmButtonText: "Open Settings",
    onConfirm: () => {
      Platform.openAppPermissionSettings();
    },
  });
}

Dispatch.on("START_VOGNITION_LISTENER_FROM_STARTUP", () => {
  setValueAtPath("vognition.listenOnStartupPending", false);

  if (
    isVognitionSupported &&
    LocalSettings.getShowVognition() &&
    LocalSettings.getShowVognitionOnStartup()
  ) {
    Dispatch("START_VOGNITION_LISTENER");
  }
});

Dispatch.on("START_VOGNITION_LISTENER", () => {
  if (!isVognitionSupported) {
    return;
  }
  if (!profileUpdated) {
    shouldStartOnProfileUpdated = true;
    return;
  }

  Platform.checkMicrophonePermissions().then((status) => {
    if (status === "granted") {
      startListenerWithTimeout();
    } else if (status === "undetermined") {
      // Since it's undetermined, we've never requested permission before, so
      // starting the listener will request permission.
      startListenerWithTimeout();
    } else if (status === "denied") {
      showVoicePermissionInstructionDialog();
    }
  });
});

Dispatch.on("STOP_VOGNITION_LISTENER", () => {
  if (!isVognitionSupported) {
    return;
  }

  clearTimeout(vognitionListenerTimeout);
  Platform.stopVognitionListener();
});

const handleVognitionDetail = _.debounce((detail) => {
  Platform.speakString(detail);
  Dispatch("SHOW_TOAST", detail);
}, 1000);

let fakeLoadingTimer;
const eventuallyStopListener = (loading) => {
  // On iOS there are certain commands which never properly close themselves.
  // Namely, commands which respond "Please make your command more specific".
  // Normal, happy-path behavior is to detail the action Vognition will perform
  // and then "Success" or "Failure" a couple seconds later, which comes with
  // "loading: false" and properly stops the process. Cancel the process if
  // we never (racey!) receive the response.
  if (loading === false) {
    // We properly finished a command, so we don't need to clean up later
    clearTimeout(fakeLoadingTimer);
  } else if (loading === true) {
    // We received an update with text, but it still wants to show the loading
    // spinner. Either we'll soon get a new update that doesn't, or we should
    // cancel the command in a few seconds.
    clearTimeout(fakeLoadingTimer);
    fakeLoadingTimer = setTimeout(() => {
      Platform.stopVognitionListener();
    }, 10000);
  }
};

Dispatch.on("VOGNITION_STATE_UPDATE", ({ recording, loading, detail }) => {
  if (
    Store.vognition.isMicrophoneButtonActive === true &&
    recording === false
  ) {
    // We are transitioning from recording to not recording. We should not
    // call stopVognitionListener in ~10 seconds.
    clearTimeout(vognitionListenerTimeout);
  }
  setValueAtPath("vognition.isMicrophoneButtonActive", Boolean(recording));
  setValueAtPath("vognition.isMicrophoneButtonLoading", Boolean(loading));
  if (typeof detail === "string" && detail.trim().length > 0) {
    handleVognitionDetail(detail);
    eventuallyStopListener(loading);
  }
});



// WEBPACK FOOTER //
// ./src/flux_like/action_handlers/vognition.js